import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  listarProyectos,
  listarClientes,
  listarArchivosPaso,
  descargarArchivoS3,
} from "./s3Cliente";
import { pasos, PASO_BOM, NOMBRE_DATOS_BOM } from "./CreacionDocumentacion";
import "../../../incidentes.css";

const MARCADOR_VACIO = "—";
const MS_POR_DIA = 24 * 60 * 60 * 1000;
const UMBRAL_ALERTA_DIAS = 2;
const TAMANIO_PAGINA = 10;

const COLUMNAS = [
  { clave: "proyecto", etiqueta: "Proyecto" },
  { clave: "cliente", etiqueta: "Cliente" },
  { clave: "empresa", etiqueta: "Empresa" },
  { clave: "paso", etiqueta: "Paso actual" },
  { clave: "estado", etiqueta: "Estado" },
  { clave: "fechaLimite", etiqueta: "Fecha límite" },
  { clave: "responsable", etiqueta: "Responsable" },
];

const medianoche = (fecha) => {
  const resultado = new Date(fecha);
  resultado.setHours(0, 0, 0, 0);
  return resultado;
};

const diferenciaDias = (fechaObjetivo, fechaBase) => Math.round((medianoche(fechaObjetivo) - medianoche(fechaBase)) / MS_POR_DIA);

const formatearFecha = (fecha) => fecha.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });

const calcularFechaLimite = (proyecto) => {
  if (!proyecto.fechaCreacion || !proyecto.duracionDias) return null;
  const base = medianoche(proyecto.fechaCreacion);
  const limite = new Date(base);
  limite.setDate(limite.getDate() + Math.round(proyecto.duracionDias));
  return limite;
};

const nivelAlerta = (dias) => {
  if (dias === null || dias === undefined) return null;
  if (dias < 0) return "vencido";
  if (dias <= UMBRAL_ALERTA_DIAS) return "proximo";
  return "ok";
};

const TEXTO_FECHA = {
  vencido: (dias) => `Vencido hace ${Math.abs(dias)} día(s)`,
  proximo: (dias) => (dias === 0 ? "Vence hoy" : `Vence en ${dias} día(s)`),
  ok: () => null,
};

const ICONO_FECHA = { vencido: "⛔", proximo: "⏳", ok: "" };

const iniciales = (nombre) =>
  nombre
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((parte) => parte[0]?.toUpperCase() || "")
    .join("");

const obtenerCorreoIngenieroRespaldo = async (proyecto) => {
  try {
    const version = (proyecto.versionesPorPaso && proyecto.versionesPorPaso[PASO_BOM]) || 1;
    const archivos = await listarArchivosPaso({
      proyectoId: proyecto.id,
      pasoNumero: PASO_BOM,
      tituloPaso: pasos.find((p) => p.numero === PASO_BOM).titulo,
      version,
    });
    const archivoDatos = archivos.find((a) => a.tipo === "datos" && a.nombreArchivo === NOMBRE_DATOS_BOM);
    if (!archivoDatos) return null;
    const blob = await descargarArchivoS3(archivoDatos.key);
    const texto = await blob.text();
    const datos = JSON.parse(texto);
    return datos.correoIngeniero || null;
  } catch (error) {
    return null;
  }
};

const TableroProyectos = () => {
  const navigate = useNavigate();
  const [proyectos, setProyectos] = useState([]);
  const [clientesPorId, setClientesPorId] = useState({});
  const [responsablesRespaldo, setResponsablesRespaldo] = useState({});
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [mostrarFinalizados, setMostrarFinalizados] = useState(false);
  const [contadorRecarga, setContadorRecarga] = useState(0);
  const [busqueda, setBusqueda] = useState("");
  const [ordenColumna, setOrdenColumna] = useState("fechaLimite");
  const [ordenDireccion, setOrdenDireccion] = useState("asc");
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    let cancelado = false;

    const cargar = async () => {
      setCargando(true);
      setError("");
      try {
        const [listaProyectos, listaClientes] = await Promise.all([
          listarProyectos(),
          listarClientes(),
        ]);
        if (cancelado) return;
        setProyectos(listaProyectos);
        setClientesPorId(
          Object.fromEntries(listaClientes.map((c) => [c.id, c.nombre]))
        );
        setResponsablesRespaldo({});
      } catch (err) {
        console.error("No se pudo cargar el tablero de proyectos.", err);
        if (!cancelado) setError("No se pudieron cargar los proyectos. Intenta de nuevo.");
      } finally {
        if (!cancelado) setCargando(false);
      }
    };

    cargar();
    return () => {
      cancelado = true;
    };
  }, [contadorRecarga]);

  const filasTodas = useMemo(
    () =>
      proyectos.map((proyecto) => ({ proyecto, fechaLimite: calcularFechaLimite(proyecto) })),
    [proyectos]
  );

  const resumen = useMemo(() => {
    const activas = filasTodas.filter((f) => !f.proyecto.completado);
    const contarNivel = (nivel) =>
      activas.filter((f) => {
        const dias = f.fechaLimite ? diferenciaDias(f.fechaLimite, new Date()) : null;
        return nivelAlerta(dias) === nivel;
      }).length;
    return {
      activos: activas.length,
      proximos: contarNivel("proximo"),
      vencidos: contarNivel("vencido"),
      finalizados: filasTodas.length - activas.length,
    };
  }, [filasTodas]);

  const coincideBusqueda = (proyecto) => {
    const termino = busqueda.trim().toLowerCase();
    if (!termino) return true;
    const nombreCliente = (clientesPorId[proyecto.clienteId] || "").toLowerCase();
    return (
      (proyecto.nombre || "").toLowerCase().includes(termino) ||
      nombreCliente.includes(termino)
    );
  };

  const filasFiltradas = useMemo(
    () =>
      filasTodas.filter(
        (f) => (mostrarFinalizados || !f.proyecto.completado) && coincideBusqueda(f.proyecto)
      ),
    [filasTodas, mostrarFinalizados, busqueda, clientesPorId]
  );

  const valorOrden = (fila, columna) => {
    const { proyecto, fechaLimite } = fila;
    switch (columna) {
      case "proyecto":
        return (proyecto.nombre || "").toLowerCase();
      case "cliente":
        return (clientesPorId[proyecto.clienteId] || "").toLowerCase();
      case "empresa":
        return (proyecto.empresa || "").toLowerCase();
      case "paso":
        return proyecto.pasoMaximoAlcanzado || 1;
      case "estado":
        return proyecto.completado ? 1 : 0;
      case "responsable":
        return (proyecto.firmante?.nombre || responsablesRespaldo[proyecto.id] || "").toLowerCase();
      case "fechaLimite":
      default:
        return fechaLimite ? fechaLimite.getTime() : null;
    }
  };

  const filasOrdenadas = useMemo(() => {
    return [...filasFiltradas].sort((a, b) => {
      const va = valorOrden(a, ordenColumna);
      const vb = valorOrden(b, ordenColumna);
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      const comparacion =
        typeof va === "number" ? va - vb : String(va).localeCompare(String(vb), "es");
      return ordenDireccion === "asc" ? comparacion : -comparacion;
    });
  }, [filasFiltradas, ordenColumna, ordenDireccion, clientesPorId, responsablesRespaldo]);

  const totalPaginas = Math.max(1, Math.ceil(filasOrdenadas.length / TAMANIO_PAGINA));

  useEffect(() => {
    setPagina(1);
  }, [busqueda, mostrarFinalizados, ordenColumna, ordenDireccion]);

  useEffect(() => {
    if (pagina > totalPaginas) setPagina(totalPaginas);
  }, [totalPaginas, pagina]);

  const filasPagina = useMemo(
    () => filasOrdenadas.slice((pagina - 1) * TAMANIO_PAGINA, pagina * TAMANIO_PAGINA),
    [filasOrdenadas, pagina]
  );

  const alternarOrden = (columna) => {
    if (ordenColumna === columna) {
      setOrdenDireccion((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setOrdenColumna(columna);
      setOrdenDireccion("asc");
    }
  };

  useEffect(() => {
    let cancelado = false;

    const pendientes = filasFiltradas
      .map((f) => f.proyecto)
      .filter((p) => !p.firmante?.nombre && !(p.id in responsablesRespaldo));

    if (pendientes.length === 0) return;

    Promise.allSettled(pendientes.map((p) => obtenerCorreoIngenieroRespaldo(p))).then(
      (resultados) => {
        if (cancelado) return;
        setResponsablesRespaldo((prev) => {
          const siguiente = { ...prev };
          pendientes.forEach((proyecto, indice) => {
            const resultado = resultados[indice];
            siguiente[proyecto.id] =
              resultado.status === "fulfilled" ? resultado.value : null;
          });
          return siguiente;
        });
      }
    );

    return () => {
      cancelado = true;
    };
  }, [filasFiltradas, responsablesRespaldo]);

  return (
    <div className="docgen-tablero-contenedor">
      <div className="docgen-tablero-header">
        <div>
          <h2 className="docgen-tablero-titulo">Tablero de proyectos</h2>
          <p className="docgen-tablero-subtitulo">
            Vista consolidada de los proyectos gestionados en Creación de Documentación.
          </p>
        </div>
        <div className="docgen-tablero-acciones">
          <input
            type="text"
            className="docgen-tablero-busqueda"
            placeholder="Buscar por proyecto o cliente..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
          <label className="docgen-tablero-toggle">
            <input
              type="checkbox"
              checked={mostrarFinalizados}
              onChange={(e) => setMostrarFinalizados(e.target.checked)}
            />
            Mostrar finalizados
          </label>
          <button
            type="button"
            className="docgen-tablero-btn-actualizar"
            onClick={() => setContadorRecarga((n) => n + 1)}
            disabled={cargando}
          >
            🔄 Actualizar
          </button>
        </div>
      </div>

      {!cargando && !error && (
        <div className="docgen-tablero-kpis">
          <div className="docgen-tablero-kpi">
            <span className="docgen-tablero-kpi-valor">{resumen.activos}</span>
            <span className="docgen-tablero-kpi-label">Proyectos activos</span>
          </div>
          <div className="docgen-tablero-kpi docgen-tablero-kpi--proximo">
            <span className="docgen-tablero-kpi-valor">{resumen.proximos}</span>
            <span className="docgen-tablero-kpi-label">⏳ Próximos a vencer</span>
          </div>
          <div className="docgen-tablero-kpi docgen-tablero-kpi--vencido">
            <span className="docgen-tablero-kpi-valor">{resumen.vencidos}</span>
            <span className="docgen-tablero-kpi-label">⛔ Vencidos</span>
          </div>
          <div className="docgen-tablero-kpi docgen-tablero-kpi--finalizado">
            <span className="docgen-tablero-kpi-valor">{resumen.finalizados}</span>
            <span className="docgen-tablero-kpi-label">🏁 Finalizados</span>
          </div>
        </div>
      )}

      {cargando && <p className="docgen-tablero-estado-vacio">Cargando proyectos...</p>}
      {!cargando && error && <p className="docgen-alerta docgen-alerta--vencido">{error}</p>}

      {!cargando && !error && filasOrdenadas.length === 0 && (
        <p className="docgen-tablero-estado-vacio">
          {busqueda.trim()
            ? "No hay proyectos que coincidan con la búsqueda."
            : `No hay proyectos ${mostrarFinalizados ? "" : "activos "}para mostrar.`}
        </p>
      )}

      {!cargando && !error && filasOrdenadas.length > 0 && (
        <div className="docgen-tablero-tabla-wrap">
          <table className="docgen-tablero-tabla">
            <thead>
              <tr>
                {COLUMNAS.map((columna) => (
                  <th
                    key={columna.clave}
                    className="docgen-tablero-th-ordenable"
                    onClick={() => alternarOrden(columna.clave)}
                  >
                    {columna.etiqueta}
                    {ordenColumna === columna.clave ? (ordenDireccion === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filasPagina.map(({ proyecto, fechaLimite }) => {
                const pasoActual = pasos.find(
                  (p) => p.numero === (proyecto.pasoMaximoAlcanzado || 1)
                );
                const dias = fechaLimite ? diferenciaDias(fechaLimite, new Date()) : null;
                const nivel = proyecto.completado ? null : nivelAlerta(dias);
                const responsable =
                  proyecto.firmante?.nombre || responsablesRespaldo[proyecto.id] || null;

                return (
                  <tr
                    key={proyecto.id}
                    className="docgen-tablero-fila-click"
                    onClick={() =>
                      navigate(`/creacion-documentacion?proyecto=${encodeURIComponent(proyecto.id)}`)
                    }
                  >
                    <td className="docgen-tablero-celda-proyecto">
                      {proyecto.nombre || MARCADOR_VACIO}
                    </td>
                    <td>{clientesPorId[proyecto.clienteId] || MARCADOR_VACIO}</td>
                    <td>
                      {proyecto.empresa ? (
                        <span className="docgen-tablero-chip-empresa">{proyecto.empresa}</span>
                      ) : (
                        MARCADOR_VACIO
                      )}
                    </td>
                    <td>
                      <div className="docgen-tablero-paso">
                        <span className="docgen-tablero-paso-numero">
                          {proyecto.pasoMaximoAlcanzado || 1}/{pasos.length}
                        </span>
                        {pasoActual ? pasoActual.titulo : MARCADOR_VACIO}
                      </div>
                    </td>
                    <td>
                      <span
                        className={
                          "docgen-tablero-badge " +
                          (proyecto.completado
                            ? "docgen-tablero-badge--finalizado"
                            : "docgen-tablero-badge--activo")
                        }
                      >
                        {proyecto.completado ? "🏁 Finalizado" : "● Activo"}
                      </span>
                    </td>
                    <td>
                      {fechaLimite ? (
                        <div
                          className={
                            "docgen-tablero-fecha" + (nivel ? ` docgen-tablero-fecha--${nivel}` : "")
                          }
                        >
                          <span className="docgen-tablero-fecha-valor">
                            {ICONO_FECHA[nivel] || ""} {formatearFecha(fechaLimite)}
                          </span>
                          {nivel && TEXTO_FECHA[nivel](dias) && (
                            <span className="docgen-tablero-fecha-hint">
                              {TEXTO_FECHA[nivel](dias)}
                            </span>
                          )}
                        </div>
                      ) : (
                        MARCADOR_VACIO
                      )}
                    </td>
                    <td>
                      {responsable ? (
                        <div className="docgen-tablero-responsable">
                          <span className="docgen-tablero-avatar">{iniciales(responsable)}</span>
                          <span>{responsable}</span>
                        </div>
                      ) : (
                        MARCADOR_VACIO
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!cargando && !error && totalPaginas > 1 && (
        <div className="docgen-tablero-paginacion">
          <button
            type="button"
            className="docgen-tablero-paginacion-btn"
            onClick={() => setPagina((p) => Math.max(1, p - 1))}
            disabled={pagina === 1}
          >
            ← Anterior
          </button>
          <span className="docgen-tablero-paginacion-info">
            Página {pagina} de {totalPaginas}
          </span>
          <button
            type="button"
            className="docgen-tablero-paginacion-btn"
            onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
            disabled={pagina === totalPaginas}
          >
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
};

export default TableroProyectos;
